/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import type { ProductSlug } from "@/data/productCatalog";

/**
 * V-3e — one conversion destination per product.
 *
 * Every download used to be reported against a single conversion action, so a
 * campaign could not tell a screen recorder download from a translator
 * download. Each product now has its own destination.
 *
 * **Why this is a `Record<ProductSlug, ...>` and not a plain object.** The
 * original proposal was keyed on product names, and two of the ten were names
 * this site does not use — `webcam-monitor` instead of `wcm`, and
 * `dictation-pro` instead of `dictation`. Nothing would have errored: the
 * lookup returns nothing, the code returns early, and those two products simply
 * never appear in Google Ads. Nobody finds out until somebody asks why WebCam
 * Monitor has no downloads.
 *
 * Typed this way, a wrong name fails the build. Adding a product to the
 * catalogue without giving it a destination fails the build too.
 *
 * **Why the values come from the environment.** A conversion label is not a
 * secret, but it is a per-account value, and pointing test traffic at the
 * production advertising account produces conversions that look real and are
 * not. Keeping them in the environment means the feature and staging sites can
 * be pointed at a test account, or at nothing at all, without a code change.
 *
 * Each name is written out in full rather than built from the product name,
 * because the build tool replaces these references at build time and can only
 * do that when it can see the whole name in the source.
 *
 * The internal product codes are recorded beside each entry. They are what the
 * Google Ads console shows, so they are what somebody cross-checking this list
 * against the console will be reading.
 */
const PER_PRODUCT_LABELS: Record<ProductSlug, string | undefined> = {
  // Dictation Pro — internal code: dictation
  dictation: import.meta.env.VITE_GA_ADS_LABEL_DICTATION,
  // Document Translator — internal code: dt
  "document-translator": import.meta.env.VITE_GA_ADS_LABEL_DOCUMENT_TRANSLATOR,
  // Auto FTP Manager — internal code: afm
  "ftp-client": import.meta.env.VITE_GA_ADS_LABEL_FTP_CLIENT,
  // FTP Manager Free — internal code: fml
  "ftp-manager-free": import.meta.env.VITE_GA_ADS_LABEL_FTP_MANAGER_FREE,
  // WebCam Monitor — internal code: wcm
  wcm: import.meta.env.VITE_GA_ADS_LABEL_WCM,
  // IP Camera Viewer — internal code: ipcv
  "ip-camera-viewer": import.meta.env.VITE_GA_ADS_LABEL_IP_CAMERA_VIEWER,
  // Paper Scanner Pro — internal code: psp
  "paper-scanner-pro": import.meta.env.VITE_GA_ADS_LABEL_PAPER_SCANNER_PRO,
  // My Screen Recorder Pro — internal code: msr
  "screen-recorder": import.meta.env.VITE_GA_ADS_LABEL_SCREEN_RECORDER,
  // Text Speaker — internal code: ts
  "text-to-speech-software": import.meta.env
    .VITE_GA_ADS_LABEL_TEXT_TO_SPEECH_SOFTWARE,
  // Security Monitor Pro — internal code: smp
  "video-surveillance-software": import.meta.env
    .VITE_GA_ADS_LABEL_VIDEO_SURVEILLANCE_SOFTWARE,
};

/** Every product name that has a conversion destination slot. */
export const CONVERSION_LABEL_SLUGS = Object.keys(
  PER_PRODUCT_LABELS,
) as ProductSlug[];

/**
 * The conversion label for one product, or the shared one.
 *
 * Falls back to the original single label when a product has no destination of
 * its own. Some signal is better than none: a download still counts, it just
 * cannot be told apart from the others until the per-product value is set.
 */
export function resolveConversionLabel(
  slug: string | undefined | null,
  fallbackLabel: string | undefined,
): string | undefined {
  if (slug && slug in PER_PRODUCT_LABELS) {
    const label = PER_PRODUCT_LABELS[slug as ProductSlug];
    if (label && label.trim() !== "" && !label.startsWith("%")) {
      return label;
    }
  }
  return fallbackLabel;
}
